import { Typography } from 'antd';
import { useFindAllContactSentiments, useFindAllContactSentimentsByChat, useOmniChatSessionsWaitingList } from 'hooks';
import { omniChatMessages } from 'messages';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { InView } from 'react-intersection-observer';
import {
  offReceiveChatSessionWaiting,
  offRemoveChatSessionWaiting,
  onReceiveChatSessionWaiting,
  onRemoveChatSessionWaiting
} from 'services';
import { ChatSessionDto } from 'types';
import OmniChatSessionsWaitingAssignItem from './OmniChatSessionsWaitingAssignItem';

type OmniChatSessionsWaitingProps = {
  onSelectCustomer?: (value?: ChatSessionDto) => void;
  selectedCustomerId?: number;
  endpoint?: string;
};

export type OmniChatSessionsWaitingRefProps = {
  handleRefetchWaiting: () => void;
};

const OmniChatSessionsWaiting = forwardRef<OmniChatSessionsWaitingRefProps, OmniChatSessionsWaitingProps>(
  ({ onSelectCustomer, selectedCustomerId, endpoint }, ref) => {
    useImperativeHandle(ref, () => ({
      handleRefetchWaiting
    }));

    const { chatSessionWaiting, refetch, isFetching, isLoading, hasMore, handleLoadMore, count } =
      useOmniChatSessionsWaitingList({
        keyword: '',
        endpoint
      });

    const {
      contactSentiments: chatSessionWaitingSentiments,
      isLoadingContactSentiments,
      isFetchingContactSentiments,
      refetchContactSentiments
    } = useFindAllContactSentimentsByChat({
      chatIds: chatSessionWaiting?.map((item) => item.chatId)
    });

    const handleRefetchWaiting = () => {
      refetch();
    };

    useEffect(() => {
      onReceiveChatSessionWaiting(() => {});
      onRemoveChatSessionWaiting(() => {});

      return () => {
        offRemoveChatSessionWaiting();
        offReceiveChatSessionWaiting();
      };
    }, []);

    useEffect(()=>{
      if(!selectedCustomerId&&chatSessionWaiting.length>0){
        onSelectCustomer?.(chatSessionWaiting?.[0]);
      }
    },[chatSessionWaiting])

    return (
      <div className='flex max-h-[calc(100vh-220px)] flex-1 flex-col overflow-hidden'>
        <div className='flex h-16 shrink-0 items-center border-b px-5'>
          <Typography.Text className='mb-0 text-lg font-semibold'>
            <span className='text-2xl'>{count} </span>
            {omniChatMessages.waitingCustomers}
          </Typography.Text>
        </div>

        <div className='max-h-full w-full overflow-y-auto overflow-x-hidden'>
          {chatSessionWaiting?.map((item: ChatSessionDto) => (
            <OmniChatSessionsWaitingAssignItem
              onSelect={() => onSelectCustomer?.(item)}
              key={item?.chatSessionId}
              chatSession={item}
              active={item?.chatSessionId === selectedCustomerId}
              sentiment={
                chatSessionWaitingSentiments?.find((sentiment) => sentiment?.chatId && sentiment.chatId === item.chatId)
                  ?.sentiment || undefined
              }
              isLoadingSentiment={isLoadingContactSentiments || isFetchingContactSentiments}
            />
          ))}
          {!isLoading && hasMore && !isFetching ? (
            <InView className='h-0 w-full' as='div' onChange={(inView) => inView && handleLoadMore()} />
          ) : null}
        </div>
      </div>
    );
  }
);

export default OmniChatSessionsWaiting;
